/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

/** Match option labels (English storefronts; extend if needed). */
const STICKY_SIZE_NAMES = { size: 1, sizes: 1, 'μέγεθος': 1, 'μεγεθος': 1 };
const STICKY_COLOR_NAMES = { color: 1, colour: 1, 'χρώμα': 1 };

const fryklandWatchers = new WeakMap();

/** Visible Frykland price only — never read script/style text from the app block. */
const STICKY_FRYKLAND_PRICE_RE = /(?:€|£|\$)\s*[\d][\d.,]*|[\d][\d.,]*\s*(?:€|£|\$)/;

/**
 * Build sticky ATC copy from Frykland app text (locale prefix from Liquid; price from app block).
 * @param {Element|null} source
 * @param {string} [prefix]
 * @returns {string}
 */
function stickyFryklandLabel(source, prefix) {
  if (!source) return '';

  const clone = source.cloneNode(true);
  clone.querySelectorAll('script, style, noscript').forEach((el) => el.remove());

  const text = clone.textContent.replace(/\s+/g, ' ').trim();
  if (!text) return '';

  const priceMatch = text.match(STICKY_FRYKLAND_PRICE_RE);
  if (!priceMatch) return '';

  const price = priceMatch[0].replace(/\s+/g, ' ').trim();
  const labelPrefix = (prefix || '30-day lowest:').trim();
  return `${labelPrefix} ${price}`;
}

/**
 * Mirror Frykland 30-day lowest price from the in-page app block into sticky ATC.
 * @param {Element|null} section
 */
function syncStickyFrykland(section) {
  section = section || document.querySelector('.section-main-product');
  if (!section) return;

  const source = section.querySelector('.product-info .pdp-app-block--frykland');
  const target = section.querySelector('sticky-atc .sticky-atc__frykland');
  if (!target) return;

  const label = stickyFryklandLabel(source, target.dataset.thirtyDayLowest);
  if (!label) {
    target.hidden = true;
    target.replaceChildren();
    return;
  }

  target.textContent = label;
  target.hidden = false;
}

/**
 * Re-sync when the Frykland app block updates asynchronously.
 * @param {Element} section
 */
function watchStickyFryklandSource(section) {
  if (!section || fryklandWatchers.has(section)) return;

  const source = section.querySelector('.product-info .pdp-app-block--frykland');
  if (!source) return;

  const observer = new MutationObserver(() => {
    syncStickyFrykland(section);
  });
  observer.observe(source, { childList: true, subtree: true, characterData: true });
  fryklandWatchers.set(section, observer);
}

/** English-only copy for sticky ATC (no locale files). */
const STICKY_ATC_COPY = {
  SELECT_SIZE_AND_COLOR: 'Select size and color',
  SELECT_SIZE: 'Select size',
  SELECT_COLOR: 'Select color',
  SELECT_OPTIONS: 'Select options'
};

/** Pixels below sticky header: show bottom bar once main ATC bottom is at or above this line. */
const STICKY_ATC_HEADER_CLEARANCE_PX = 20;
/** Mobile: extra scroll before sticky bar — applied to size row / buy row, not the eager ar.top shortcut. */
const STICKY_ATC_MOBILE_SWATCH_CLEARANCE_PX = 20;
const STICKY_ATC_MOBILE_MAX_WIDTH = 768;

function stickyNorm(s) {
  return String(s || '').trim().toLowerCase();
}

function stickyOptionSelected(el) {
  if (el.dataset.selectorType === 'dropdown') {
    const selected = el.querySelector('.custom-select__option[aria-selected="true"]');
    return !!(selected && selected.dataset.value !== '');
  }
  return !!el.querySelector('input.js-option:checked');
}

/**
 * @param {Element} picker
 * @returns {{ sizeEl: Element|null, colorEl: Element|null }}
 */
function stickyFindSizeColorSelectors(picker) {
  let sizeEl = null;
  let colorEl = null;
  picker.querySelectorAll('.option-selector:not(.option-selector--custom)').forEach((el) => {
    const opt = el.dataset.option;
    if (!opt) return;
    const k = stickyNorm(opt);
    if (STICKY_SIZE_NAMES[k]) sizeEl = el;
    if (STICKY_COLOR_NAMES[k]) colorEl = el;
  });
  return { sizeEl, colorEl };
}

/**
 * @param {Element} picker
 * @returns {string[]}
 */
function stickyGetAllMissingOptions(picker) {
  const missing = [];
  picker.querySelectorAll('.option-selector:not(.option-selector--custom)').forEach((el) => {
    if (el.dataset.selectorType === 'dropdown') {
      const selected = el.querySelector('.custom-select__option[aria-selected="true"]');
      if (!selected || selected.dataset.value === '') {
        if (el.dataset.option) missing.push(el.dataset.option);
      }
    } else if (!el.querySelector('input.js-option:checked') && el.dataset.option) {
      missing.push(el.dataset.option);
    }
  });
  return missing;
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.buyButtons = this.section.querySelector('.quantity-submit-row');
        if (!this.buyButtons) return;

        this.mainProductForm = this.section.querySelector('product-form');
        this.mainProductSubmitForm = this.mainProductForm.getSubmitForm();
        /** Show sticky once this scrolls past the header — primary ATC only (not full row w/ SPB/qty). */
        this.scrollVisibilityAnchor =
          this.mainProductForm?.querySelector('.quantity-submit-row__submit button.add-to-cart[name="add"]') ||
          this.mainProductForm?.querySelector('.quantity-submit-row button.add-to-cart[name="add"]') ||
          this.buyButtons;
        this.descriptionRevealEl =
          this.section.querySelector('[data-dynamic-variant-content="description"].not-in-quickbuy') ||
          this.section.querySelector('[data-dynamic-variant-content="description"]');
        this.submitForm = this.getSubmitForm();
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));
        this.button = this.querySelector('.add-to-cart');

        this.throttledOnScroll = throttle(this.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));

        this.boundUpdateAddToCartButton = () => {
          if (this._updateAtcRaf) cancelAnimationFrame(this._updateAtcRaf);
          this._updateAtcRaf = requestAnimationFrame(() => {
            this._updateAtcRaf = 0;
            this.updateAddToCartButton();
          });
        };
        this.boundSyncFrykland = () => {
          syncStickyFrykland(this.section);
        };
        this.boundSyncFryklandDelayed = () => {
          window.setTimeout(this.boundSyncFrykland, 50);
          window.setTimeout(this.boundSyncFrykland, 350);
        };
        this.section.addEventListener('on:variant:change', this.boundUpdateAddToCartButton);
        this.section.addEventListener('on:variant:change', this.boundSyncFryklandDelayed);
        this.section.addEventListener('change', this.boundUpdateAddToCartButton);

        watchStickyFryklandSource(this.section);
        this.updateImage();
        this.updateAddToCartButton();
        requestAnimationFrame(() => {
          this.handleScroll();
          this.boundSyncFrykland();
          window.setTimeout(this.boundSyncFrykland, 350);
        });
      }

      disconnectedCallback() {
        if (this.throttledOnScroll) {
          window.removeEventListener('scroll', this.throttledOnScroll);
        }
        if (this.section && this.boundUpdateAddToCartButton) {
          this.section.removeEventListener('on:variant:change', this.boundUpdateAddToCartButton);
          this.section.removeEventListener('change', this.boundUpdateAddToCartButton);
        }
        if (this.section && this.boundSyncFryklandDelayed) {
          this.section.removeEventListener('on:variant:change', this.boundSyncFryklandDelayed);
        }
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        const variantId =
          (this.mainProductSubmitForm?.querySelector('input[name="id"]')?.value || '').trim() ||
          (this.submitForm?.querySelector('input[name="id"]')?.value || '').trim();
        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            theme.scrollToRevealElement(target);
          }
          return;
        }

        const variantPicker = this.section.querySelector('variant-picker');
        const needsVariantSelection = variantPicker?.classList.contains('variant-picker--preselection');
        const customFormValid = needsVariantSelection ? this.mainProductForm.validate() : true;

        if (!customFormValid || !this.mainProductSubmitForm.reportValidity()) {
          if (!customFormValid) {
            return;
          }
          const input = Array.from(this.mainProductSubmitForm.elements).find(
            (el) => !el.checkValidity()
          );
          if (input) {
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        this.submitForm.querySelectorAll('[data-copied]').forEach((el) => el.remove());

        const formData = new FormData(this.mainProductSubmitForm);
        for (const p of formData) {
          if (!this.submitForm.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.submitForm.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      /**
       * Mobile reveal anchor: size option row — show the bar once sizes scroll past,
       * not when the main Add to Bag row leaves the viewport.
       * @returns {Element|null}
       */
      getMobileRevealAnchor() {
        const variantPicker = this.section.querySelector('variant-picker');
        if (variantPicker) {
          const { sizeEl } = stickyFindSizeColorSelectors(variantPicker);
          if (sizeEl) return sizeEl;
        }

        return this.buyButtons || this.scrollVisibilityAnchor;
      }

      /**
       * Determine visibility after scroll.
       */
      handleScroll() {
        const header = document.querySelector('.pageheader--sticky')
          ? document.querySelector('.section-header')
          : null;
        const topOffset = header ? header.offsetHeight : 0;
        const revealLine = topOffset + STICKY_ATC_HEADER_CLEARANCE_PX;
        const isMobile = window.matchMedia(`(max-width: ${STICKY_ATC_MOBILE_MAX_WIDTH - 1}px)`).matches;

        const anchor = this.scrollVisibilityAnchor || this.buyButtons;
        if (!anchor) return;

        let showBar = false;

        if (isMobile) {
          const mobileAnchor = this.getMobileRevealAnchor();
          if (mobileAnchor) {
            const mr = mobileAnchor.getBoundingClientRect();
            /** Size row bottom clears the header band (+ optional extra scroll). No `top < header` shortcut. */
            showBar = mr.bottom <= revealLine - STICKY_ATC_MOBILE_SWATCH_CLEARANCE_PX;
          }
        } else {
          const ar = anchor.getBoundingClientRect();
          const pastMainAtc = ar.bottom <= revealLine || ar.top < topOffset;

          let pastDescriptionStart = false;
          const desc = this.descriptionRevealEl;
          if (desc && !desc.hasAttribute('hidden') && desc.offsetParent !== null) {
            const dr = desc.getBoundingClientRect();
            const vh = window.innerHeight || 0;
            if (vh > 0) {
              pastDescriptionStart = dr.top < vh - 40;
            }
          }

          showBar = pastMainAtc || pastDescriptionStart;
        }

        this.classList.toggle('sticky-atc--out', !showBar);

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Updates the image.
       */
      updateImage() {
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Sticky button label: size+color pairing (English), else generic fallback.
       */
      updateAddToCartButton() {
        if (!this.button || this.button.tagName !== 'BUTTON') return;

        const strings = (typeof theme !== 'undefined' && theme.strings) || {};
        const addText = this.button.dataset.addToCartText || strings.productsProductAddToCart || 'Add to Bag';

        const variantId =
          (this.mainProductSubmitForm?.querySelector('input[name="id"]')?.value || '').trim() ||
          (this.submitForm?.querySelector('input[name="id"]')?.value || '').trim();

        const variantPicker = this.section.querySelector('variant-picker');
        if (!variantPicker) {
          this.button.textContent = variantId ? addText : this.button.dataset.unavailableText || '';
          return;
        }

        const { sizeEl, colorEl } = stickyFindSizeColorSelectors(variantPicker);

        if (sizeEl && colorEl) {
          const sizeOk = stickyOptionSelected(sizeEl);
          const colorOk = stickyOptionSelected(colorEl);

          if (!sizeOk && !colorOk) {
            this.button.textContent = STICKY_ATC_COPY.SELECT_SIZE_AND_COLOR;
            return;
          }
          if (!sizeOk && colorOk) {
            this.button.textContent = STICKY_ATC_COPY.SELECT_SIZE;
            return;
          }
          if (sizeOk && !colorOk) {
            this.button.textContent = STICKY_ATC_COPY.SELECT_COLOR;
            return;
          }

          this.button.textContent = addText;
          return;
        }

        if (sizeEl && !colorEl) {
          this.button.textContent = stickyOptionSelected(sizeEl) ? addText : STICKY_ATC_COPY.SELECT_SIZE;
          return;
        }

        if (!sizeEl && colorEl) {
          this.button.textContent = stickyOptionSelected(colorEl) ? addText : STICKY_ATC_COPY.SELECT_COLOR;
          return;
        }

        if (variantPicker.classList.contains('variant-picker--preselection')) {
          const missing = stickyGetAllMissingOptions(variantPicker);
          if (missing.length) {
            this.button.textContent = STICKY_ATC_COPY.SELECT_OPTIONS;
            return;
          }
        }

        if (!variantId) {
          this.button.textContent = this.button.dataset.unavailableText || '';
          return;
        }

        this.button.textContent = addText;
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
